import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import './MonitGPS.css';

const MonitGPSLanding = () => {

  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  // Animated counter component
  const AnimatedCounter = ({ end, suffix, duration = 2 }) => {
    const [count, setCount] = useState(0);
    const ref = React.useRef(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
      if (!isInView) return;
      
      let startTime;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = (timestamp - startTime) / (duration * 1000);
        
        if (progress < 1) {
          setCount(Math.floor(end * progress));
          requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };
      
      requestAnimationFrame(animate);
    }, [isInView, end, duration]);

    return (
      <span ref={ref} className="font-bold">
        {count}{suffix}
      </span>
    );
  };

  // Scroll reveal component
  const ScrollReveal = ({ children, delay = 0 }) => {
    const ref = React.useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    );
  };

  const features = [
    {
      title: "Monitoring w czasie rzeczywistym",
      description: "Profesjonalne urządzenia GPS całkowicie bezobsługowe z własnym zasilaniem. Kompaktowe rozmiary.",
      icon: "📍",
      gradient: "from-cyan-500/20 to-blue-500/20"
    },
    {
      title: "Zapoznanie Rajdu",
      description: "Rejestracja przebiegu trasy i liczby przejazdów. Automatyczne zliczanie oraz generowanie szczegółowych raportów po zakończeniu zapoznania.",
      icon: "🗺️",
      gradient: "from-blue-500/20 to-cyan-500/20"
    },
    {
      title: "Pomiar Prędkości",
      description: "Specjalne strefy pomiaru średniej prędkości w wrażliwych miejscach (szkoły, obszary zabudowane). Zwiększone bezpieczeństwo podczas przygotowań.",
      icon: "🚨",
      gradient: "from-cyan-600/20 to-blue-600/20"
    },
    {
      title: "Centrum Kierowania Rajdem",
      description: "Pełne wyposażenie stanowisk CKR — monitory i skonfigurowane komputery. Monitoring wszystkich pojazdów organizatora i zawodników w czasie rzeczywistym.",
      icon: "🖥️",
      gradient: "from-blue-600/20 to-cyan-600/20"
    }
  ];

  const services = [
    {
      name: "ZAPOZNANIE",
      price: "Wycena indywidualna",
      description: "Monitoring podczas zapoznania z trasą",
      features: [
        "Wydanie urządzeń GPS",
        "Monitoring pojazdów",
        "Monitoring prędkości",
        "Zapoznanie rajdu",
        "Automatyczne zliczanie przejazdów",
        "Generowanie raportów"
       
      ],
      icon: "🗺️",
      highlighted: false
    },
    {
      name: "MONITORING RAJDU",
      price: "Wycena indywidualna",
      description: "Centrum Kierowania Rajdem",
      features: [
        "Przygotowanie stanowisk dla CKR",
        "Monitoring pojazdów organizatora",
        "Monitoring zawodników",
        "Wsparcie techniczne CKR",
        "Montaż/Demontaż urządzeń na BK",
        "Wyposażenie techniczne"
      ],
      icon: "🖥️",
      highlighted: false
    },
    {
      name: "ZAPOZNANIE + MONITORING",
      price: "Pakiet kompleksowy",
      description: "Pełna obsługa wydarzenia",
      features: [
        "Wszystko z pakietu Zapoznanie",
        "Wszystko z pakietu Monitoring",
        "Dedykowany zespół 3 osobowy",
      ],
      icon: "🏆",
      highlighted: true
    }
  ];

  const requirements = {
    zapoznanie: [
      "Miejsce w biurze wraz z dostępem do prądu",
      "Informacja 2 tygodnie przed o umiejscowieniu bramek i mapy rajdu",
      "Informacje o godzinach wydawania urządzeń GPS",
      "Pobieranie opłaty za urządzenia oraz kaucję (zwrot po zwrocie urządzenia)",
      "Podanie osoby do wysłania raportu",
      "Naklejka na samochód, książka drogowa, ID dla 3 osób",
      "Nocleg dla 3 osób od OA do zbierania GPS (plus 1 dzień jeśli >200km)"
    ],
    monitoring: [
      "Miejsce w biurze wraz z dostępem do prądu",
      "Informacja 2 tygodnie przed o umiejscowieniu bramek i mapy rajdu",
      "Informacje o godzinach wydawania urządzeń GPS",
      "Pobieranie opłaty za urządzenia oraz kaucję",
      "Podanie osoby do wysłania raportu",
      "Naklejka na samochód, książka drogowa, ID dla 3 osób",
      "Nocleg dla 3 osób od OA do zbierania GPS (plus 1 dzień jeśli >200km)"
    ]
  };

  const whatWeProvide = [
    "Dostarczenie i wydawanie urządzeń podczas rajdu",
    "Dojazd na rajd we własnym zakresie",
    "Obecność osoby do czasu zwrotu urządzeń",
    "Faktura VAT z terminem płatności 7 dni",
    "Przesłanie raportu do organizatora",
    "Kontrola i wymiana wadliwych urządzeń",
    "Instrukcje i opisy do regulaminu imprezy",
    "Monitoring zaraz po odebraniu urządzenia"
  ];

  const onlineResults = [
    "Prezentacja wyników w czasie rzeczywistym",
    "Integracja z urządzeniami TAG Heuer (PZM)",
    "Bezpośredni odbiór z urządzeń pomiarowych",
    "Status załóg: start i ukończenie OS",
    "Wyświetlanie na ekranach CKR",
    "Dostęp online dla kibiców",
    "Profesjonalna prezentacja danych",
    "Zwiększone bezpieczeństwo i kontrola"
  ];

  const partners = [
    "RAJD BARBÓRKI CIESZYŃSKIEJ", "RAJD WISŁY", "RAJD MAŁOPOLSKI"
  ];
  const gallery = [
  { src: "/images/gallery/ckr-1.jpg", title: "Centrum Kierowania Rajdem", desc: "Pełny monitoring wszystkich pojazdów" },
  { src: "/images/gallery/ckr-2.jpg", title: "Mapa z trasą rajdu", desc: "Odcinki specjalne i punkty kontrolne" },
  { src: "/images/gallery/ckr-3.jpg", title: "Wszystkie informacje", desc: "Informacje o pojeździe, prędkości itd" },
  { src: "/images/gallery/ckr-4.jpg", title: "Potrzebna infrastruktura do CKR", desc: "posiadamy cały sprzęt do obsługi GPS" },
];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-carbon">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        
        <motion.div 
          className="absolute top-1/4 -left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px]"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[100px]"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        {/* Navigation */}
        <motion.nav 
          className="absolute top-0 left-0 right-0 z-50 glass-effect"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
    <img 
  src="/images/logominitgps.png" 
  alt="MonitGPS Logo" 
  className="h-12 w-auto"
/>



            </div>
            
            <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
              <a href="#features" className="hover:text-cyan-400 transition-colors">Funkcje</a>
              <a href="#services" className="hover:text-cyan-400 transition-colors">Usługi</a>
              <a href="#online-results" className="hover:text-cyan-400 transition-colors">Wyniki Online</a>
              <a href="#requirements" className="hover:text-cyan-400 transition-colors">Wymagania</a>
              <a href="#contact" className="hover:text-cyan-400 transition-colors">Kontakt</a>
              <a href="https://monitgps.pl/tracking/" className="hover:text-cyan-400 transition-colors">Rally Control</a>
              <a href="#contact">
                <button className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all">
                  Skontaktuj się
                </button>
              </a>
            </div>
          </div>
        </motion.nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.div 
              className="inline-block mb-6 px-4 py-2 glass-effect rounded-full text-sm font-medium"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-cyan-400">●</span> Profesjonalne Rozwiązanie dla Sportów Motorowych
            </motion.div>
          </motion.div>

          <motion.h1 
            className="font-display font-black text-6xl md:text-8xl leading-tight mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Monitoring <span className="text-gradient">GPS</span>
            <br />
            dla <span className="text-gradient">sportów motorowych</span>
          </motion.h1>

          <motion.p 
            className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            Profesjonalne urządzenia GPS zaprojektowane specjalnie dla sportów motorowych. 
            Całkowicie bezobsługowe z własnym zasilaniem. Kompaktowe oraz bezawaryjne
          </motion.p>

          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <a href="#contact">
              <button className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-display font-bold text-lg overflow-hidden hover:shadow-2xl hover:shadow-cyan-500/50 transition-all">
                <span className="relative z-10">Zamów ofertę</span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </button>
            </a>
            
            <a href="#features">
              <button className="group px-8 py-4 glass-effect rounded-2xl font-display font-bold text-lg hover-lift flex items-center space-x-2">
                <span>Zobacz możliwości</span>
                <span className="text-2xl group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </a>
          </motion.div>

          <motion.div 
            className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ opacity }}
          >
            <div className="w-6 h-10 border-2 border-cyan-500/50 rounded-full p-1">
              <div className="w-1.5 h-3 bg-cyan-400 rounded-full mx-auto animate-pulse"></div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 scan-line"></div>
      </section>

      {/* Live Stats Bar */}
      <ScrollReveal>
        <section className="py-12 glass-effect border-y border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="group">
                <div className="font-display font-black text-5xl md:text-6xl text-gradient mb-2">
                  100%
                </div>
                <div className="text-gray-400 font-medium">Bezobsługowe</div>
                <div className="mt-2 text-xs text-cyan-500/70">Własne zasilanie, wystarczy umieścić w pojeździe</div>
              </div>
              
              <div className="group">
                <div className="font-display font-black text-5xl md:text-6xl text-gradient mb-2">
                  Real-time
                </div>
                <div className="text-gray-400 font-medium">Monitoring na żywo</div>
                <div className="mt-2 text-xs text-cyan-500/70">Pozycja, trasa i prędkość w czasie rzeczywistym</div>
              </div>
              
              <div className="group">
                <div className="font-display font-black text-5xl md:text-6xl text-gradient mb-2">
                  Serwis
                </div>
                <div className="text-gray-400 font-medium">Wsparcie techniczne</div>
                <div className="mt-2 text-xs text-cyan-500/70">Dedykowany zespół podczas rajdu</div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Core Features - Bento Grid */}
      <section id="features" className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                Nasze możliwości
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                Kompleksowe <span className="text-gradient">rozwiązania</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Od zapoznania po centrum kierowania rajdem
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <motion.div
                  className={`glass-effect rounded-3xl p-8 hover-lift cursor-pointer relative overflow-hidden ${
                    index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
                  }`}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 hover:opacity-100 transition-opacity`}></div>
                  
                  <div className="relative z-10">
                    <div className="text-6xl mb-6">{feature.icon}</div>
                    <h3 className="font-display font-bold text-2xl md:text-3xl mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed mb-6">
                      {feature.description}
                    </p>
                    
                

                    <div className="mt-6 text-cyan-400 font-display font-bold text-sm flex items-center group">
                      <span>Dowiedz się więcej</span>
                      <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services / Pricing Section */}
      <section id="services" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>

        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                Nasze usługi
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                Wybierz <span className="text-gradient">pakiet</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Dopasowany do potrzeb Twojego wydarzenia
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {services.map((service, index) => (
              <ScrollReveal key={index} delay={index * 0.15}>
                <motion.div
                  className={`pricing-card-glow glass-effect rounded-3xl p-8 relative ${
                    service.highlighted ? 'border-2 border-cyan-500 glow-effect-strong' : ''
                  }`}
                  whileHover={{ y: -8 }}
                >
                  {service.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="px-4 py-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full text-sm font-bold">
                        NAJPOPULARNIEJSZY
                      </div>
                    </div>
                  )}

                  <div className="mb-6 text-center">
                    <div className="text-6xl mb-4">{service.icon}</div>
                    <h3 className="font-display font-bold text-2xl mb-2">{service.name}</h3>
                    <p className="text-gray-500 text-sm">{service.description}</p>
                  </div>

                  <div className="mb-8 text-center">
                    <div className="font-display font-black text-3xl text-gradient">
                      {service.price}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Skontaktuj się po szczegóły</p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <span className="text-cyan-400 mr-3 mt-1 flex-shrink-0">✓</span>
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a href="#contact">
                    <button
                      className={`w-full py-4 rounded-xl font-display font-bold transition-all ${
                        service.highlighted
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-lg hover:shadow-cyan-500/50'
                          : 'glass-effect hover:border-cyan-500'
                      }`}
                    >
                      Zamów wycenę
                    </button>
                  </a>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Online Results Section */}
      <section id="online-results" className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                Dodatkowa usługa
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                Wyniki <span className="text-gradient">Online</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Profesjonalna prezentacja wyników w czasie rzeczywistym
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8">
                <h3 className="font-display font-bold text-3xl mb-6 text-cyan-400">
                  Integracja z TAG Heuer
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Oferujemy drugą aplikację dedykowaną do obsługi wyników online. 
                  Nasze rozwiązanie pozwala na prezentację wyników rajdu w czasie rzeczywistym.
                </p>
                <p className="text-gray-400 leading-relaxed mb-8">
                  System umożliwia integrację z profesjonalnymi urządzeniami pomiarowymi TAG Heuer, 
                  które są standardowo wykorzystywane przez Polski Związek Motorowy (PZM).
                </p>
                
                <div className="space-y-3">
                  {onlineResults.slice(0, 4).map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1">✓</span>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="glass-effect rounded-3xl p-8">
                <h3 className="font-display font-bold text-3xl mb-6 text-cyan-400">
                  Wyniki + CKR = Pełna Kontrola
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Dzięki integracji systemu wyników z usługą śledzenia GPS jesteśmy w stanie 
                  na jednej platformie prezentować pełny status załóg w czasie rzeczywistym.
                </p>
                <p className="text-gray-400 leading-relaxed mb-8">
                  Nasze rozwiązanie pozwala na natychmiastowe wyświetlanie informacji o załogach, 
                  które wystartowały oraz tych, które ukończyły odcinek specjalny.
                </p>
                
                <div className="space-y-3">
                  {onlineResults.slice(4).map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1">✓</span>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.6}>
            <div className="mt-12 text-center">
              <a href="#contact">
                <button className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-display font-bold text-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all">
                  Zapytaj o wyniki online →
                </button>
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Requirements Section */}
      <section id="requirements" className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                Współpraca
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                Wymagania i <span className="text-gradient">co zapewniamy</span>
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-2xl">
                    📋
                  </div>
                  <h3 className="font-display font-bold text-2xl">Wymagania od organizatora</h3>
                </div>
                
                <ul className="space-y-4">
                  {requirements.zapoznanie.map((req, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1 flex-shrink-0">•</span>
                      <span className="text-gray-300">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="glass-effect rounded-3xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-2xl">
                    ✓
                  </div>
                  <h3 className="font-display font-bold text-2xl">Co zapewniamy</h3>
                </div>
                
                <ul className="space-y-4">
                  {whatWeProvide.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1 flex-shrink-0">✓</span>
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.6}>
            <div className="mt-12 glass-effect rounded-3xl p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="text-5xl mb-4">⚡</div>
                <h3 className="font-display font-bold text-2xl mb-4">
                  Urządzenia działają zaraz po odebraniu
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  Monitoring pojazdów odbywa się natychmiast po odebraniu urządzenia. 
                  Każde urządzenie posiada wbudowaną baterię i jest gotowe do pracy od razu.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Screenshot Gallery Placeholder */}
      <section className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                System w akcji
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                Zobacz <span className="text-gradient">nasz system</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Zrzuty ekranu z aplikacji i centrum kierowania rajdem
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
    {gallery.map((item, index) => (
  <ScrollReveal key={index} delay={index * 0.15}>
    <motion.div
      className="glass-effect rounded-3xl overflow-hidden hover-lift"
      whileHover={{ scale: 1.02 }}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={item.src}
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 p-6">
          <div className="font-display font-bold text-2xl text-cyan-400 mb-1">{item.title}</div>
          <div className="text-gray-200/80">{item.desc}</div>
        </div>
      </div>

      <div className="p-6 border-t border-cyan-500/10">
        <h3 className="font-display font-bold text-xl mb-2">{item.title}</h3>
        <p className="text-gray-400">{item.desc}</p>
      </div>
    </motion.div>
  </ScrollReveal>
))}

          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                Zaufali nam
              </div>
              <h2 className="font-display font-black text-4xl md:text-6xl mb-6">
                Organizatorzy z całej Polski
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
              {partners.map((partner, index) => (
                <motion.div
                  key={index}
                  className="glass-effect rounded-2xl p-8 flex items-center justify-center hover-lift cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-center">
                    <div className="font-display font-bold text-lg text-gray-400">
                      {partner}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact" className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20"></div>
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-[150px]"
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <ScrollReveal>
            <motion.div
              animate={{ rotate: [0, 5, 0, -5, 0] }}
              transition={{ duration: 6, repeat: Infinity }}
              className="text-8xl mb-8"
            >
              🏁
            </motion.div>
            
            <h2 className="font-display font-black text-5xl md:text-8xl mb-8 leading-tight">
              Skontaktuj się <span className="text-gradient">z nami</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              Profesjonalny monitoring GPS dla Twojego rajdu. 
              Zespół MonitGPS jest do Twojej dyspozycji.
            </p>

            {/* Contact Info */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="glass-effect rounded-2xl p-6">
                <div className="text-4xl mb-3">📱</div>
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Maciek</div>
                <a href="tel:504178476" className="text-gray-300 hover:text-cyan-400 transition-colors">
                  504 178 476
                </a>
              </div>
              
              <div className="glass-effect rounded-2xl p-6">
                <div className="text-4xl mb-3">📱</div>
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Adrian</div>
                <a href="tel:791807484" className="text-gray-300 hover:text-cyan-400 transition-colors">
                  791 807 484
                </a>
              </div>
              
              <div className="glass-effect rounded-2xl p-6">
                <div className="text-4xl mb-3">📱</div>
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Dawid</div>
                <a href="tel:603197643" className="text-gray-300 hover:text-cyan-400 transition-colors">
                  603 197 643
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
              <a href="tel:504178476">
                <button className="group relative px-12 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-display font-bold text-xl overflow-hidden glow-effect-strong">
                  <span className="relative z-10 flex items-center">
                    Zadzwoń teraz
                    <span className="ml-2 text-2xl">📞</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </button>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-500">
              <div className="flex items-center">
                <span className="text-green-400 mr-2">✓</span>
                Darmowa wycena
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-2">✓</span>
                Faktura VAT 7 dni
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-2">✓</span>
                Kompleksowe wsparcie
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 glass-effect border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
    <img 
  src="/images/logominitgps.png" 
  alt="MonitGPS Logo" 
  className="h-12 w-auto"
/>

              <p className="text-gray-500 text-sm">
                Profesjonalny system monitoringu GPS dla rajdów terenowych i sportów motorowych.
              </p>
            </div>

            <div>
              <h4 className="font-display font-bold mb-4">Usługi</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#services" className="hover:text-cyan-400 transition-colors">Zapoznanie rajdu</a></li>
                <li><a href="#services" className="hover:text-cyan-400 transition-colors">Monitoring CKR</a></li>
                <li><a href="#online-results" className="hover:text-cyan-400 transition-colors">Wyniki online</a></li>
                <li><a href="#requirements" className="hover:text-cyan-400 transition-colors">Wymagania</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-display font-bold mb-4">Kontakt</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="tel:504178476" className="hover:text-cyan-400 transition-colors">Maciek: 504 178 476</a></li>
                <li><a href="tel:791807484" className="hover:text-cyan-400 transition-colors">Adrian: 791 807 484</a></li>
                <li><a href="tel:603197643" className="hover:text-cyan-400 transition-colors">Dawid: 603 197 643</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-display font-bold mb-4">System</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="hover:text-cyan-400 transition-colors">Bezobsługowe urządzenia</li>
                <li className="hover:text-cyan-400 transition-colors">Monitoring real-time</li>
                <li className="hover:text-cyan-400 transition-colors">Integracja TAG Heuer</li>
                <li className="hover:text-cyan-400 transition-colors">Wsparcie 24/7</li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between text-sm text-gray-500">
            <p>© 2024 MonitGPS. Wszystkie prawa zastrzeżone.</p>
            <div className="mt-4 md:mt-0">
              <p>Profesjonalny monitoring dla sportów motorowych</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MonitGPSLanding;